/**
 * Consent Structure Tree Types
 *
 * Type definitions for the consent structure tree hierarchy.
 * The tree has a 4-level hierarchy: Brand > Channel > Statement > Translation
 */

/**
 * Enum representing the different node types in the consent structure hierarchy
 */
export enum ConsentNodeType {
  /** Root level - represents a brand */
  BRAND = 1,
  /** Second level - represents a channel (parent or child) */
  CHANNEL = 2,
  /** Third level - represents a consent statement */
  STATEMENT = 3,
  /** Fourth level - represents a statement translation */
  STATEMENT_TRANSLATION = 4
}

/**
 * Base interface for a node in the consent structure tree
 */
export interface ConsentStructureNode {
  /** Unique identifier for the node */
  id: string
  /** Display name for the node */
  name: string
  /** Type of node in the hierarchy */
  type: ConsentNodeType
  /** MDI icon name for the node */
  icon: string
  /** CSS class for icon colour */
  color: string
  /** Child nodes */
  children?: ConsentStructureNode[]
}

/**
 * Brand node - root level of the hierarchy
 */
export interface BrandNode extends ConsentStructureNode {
  type: ConsentNodeType.BRAND
  /** Brand ID from the system */
  brandId: number
}

/**
 * Channel node - can be parent or child channel
 */
export interface ChannelNode extends ConsentStructureNode {
  type: ConsentNodeType.CHANNEL
  /** Channel ID from the system */
  channelId: number
  /** Channel name */
  channelName: string
  /** Brand ID this channel belongs to */
  brandId: number
  /** Whether this is a parent channel (has child channels) */
  isParentChannel: boolean
  /** Parent channel ID if this is a child channel */
  parentChannelId?: number
}

/**
 * Statement node - consent statement
 */
export interface StatementNode extends ConsentStructureNode {
  type: ConsentNodeType.STATEMENT
  /** Statement ID from the system */
  statementId: number
  /** Full statement text */
  statementText: string
  /** Whether this is an opt-in statement */
  isOptIn: boolean
  /** Channel ID this statement belongs to */
  channelId: number
}

/**
 * Statement translation node - translated version of a statement
 */
export interface StatementTranslationNode extends ConsentStructureNode {
  type: ConsentNodeType.STATEMENT_TRANSLATION
  /** Statement version ID */
  svid: number
  /** Full translated statement text */
  statementText: string
  /** Language ID */
  languageId: number
  /** Language name */
  languageName: string
  /** Parent statement ID */
  statementId: number
}

/**
 * Union type for all node types
 */
export type AnyConsentNode =
  | BrandNode
  | ChannelNode
  | StatementNode
  | StatementTranslationNode

/**
 * Icon and colour configuration for each node type
 */
export const NODE_TYPE_CONFIG: Record<ConsentNodeType, { icon: string; color: string; label: string }> = {
  [ConsentNodeType.BRAND]: {
    icon: 'mdi-web',
    color: 'syrenis-black--text text--lighten-2',
    label: 'Brand'
  },
  [ConsentNodeType.CHANNEL]: {
    icon: 'mdi-circle',
    color: 'prussian-blue--text', // Parent channel colour
    label: 'Channel'
  },
  [ConsentNodeType.STATEMENT]: {
    icon: 'mdi-note-text',
    color: 'onyx--text text--lighten-1',
    label: 'Statement'
  },
  [ConsentNodeType.STATEMENT_TRANSLATION]: {
    icon: 'mdi-translate',
    color: 'cadet-gray--text',
    label: 'Statement Translation'
  }
}

/**
 * Colour for child channels (different from parent channels)
 */
export const CHILD_CHANNEL_COLOR = 'black-olive--text text--lighten-2'

/**
 * Event payload when a node is clicked
 */
export interface NodeClickEvent {
  /** The node that was clicked */
  node: ConsentStructureNode
  /** The node type */
  type: ConsentNodeType
  /** Mouse event */
  event?: MouseEvent
}

/**
 * Event payload when nodes are selected
 */
export interface NodeSelectEvent {
  /** Array of selected node IDs */
  selectedIds: string[]
  /** Array of selected nodes */
  selectedNodes: ConsentStructureNode[]
}

/**
 * Interface for Vuetify v-treeview ref methods
 * Used for type-safe ref access instead of `any`
 */
export interface VuetifyTreeviewRef {
  /** Updates all nodes to open or closed state */
  updateAll: (open: boolean) => void
}
